import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
  ButtonWithTooltip,
  LightTooltip,
  LoadingButton,
} from '@sales-center/components'
import { Mode } from '@user/components/Settings/Users/hooks/useGetLicenseInfo'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { WWMXGrid } from '@user/components/xGrid'
import { useGetColumnData } from '@user/components/Settings/Users/UsersNew/hooks/useGetColumnData'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { GridDensity, GridState } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import {
  ITenantUser,
  selectUsersFilters,
  setUsersPage,
  setUsersPerPage,
} from '@user/store/users'
import { selectUsersTotalCount } from '@user/store/users/slice'
import { useActions } from '@user/components/Settings/Users/UsersNew/hooks/useActions'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import { PeopleTalk } from '@workwave-tidal/core/illustrations/PeopleTalk'
import { Add } from '@workwave-tidal/core/icons/Add'
import LicenseInformation from './LicenseInformation'
import { selectCurrentUser } from '@user/store/authorization'
import {
  defaultUsersColumns,
  defaultUsersColVis,
} from '@user/components/Settings/Users/UsersNew/usersColumn'
import { useWWMSSOEnabled } from '@user/store/tenantFeatures/hooks/useWWMSSOEnabled'
import debounce from '@mui/material/utils/debounce'

interface Props {
  loadPending: boolean
  addDisabled: boolean
  usersLoading: boolean
  addDisabledText: string
  rolesLoadingStatus: string
  rows: ITenantUser[]
  getLicensingInfo: (mode: Mode) => void
  handleEditClick: (userId: string, initialActive?: boolean) => void
  handleDeleteRequest: (userId: string) => void
  disableDeleteTooltip: (user: ITenantUser) => string | undefined
  updateUserStatus: (user: ITenantUser, reloadUsers?: boolean) => void
  handleUpdateFromPPRequest: (externalUserId: string) => void
  setAddMode: React.Dispatch<React.SetStateAction<boolean>>
  setSearchText: React.Dispatch<React.SetStateAction<string>>
}

export const UsersNew = (props: Props) => {
  const {
    loadPending,
    addDisabled,
    usersLoading,
    addDisabledText,
    rolesLoadingStatus,
    getLicensingInfo,
    handleEditClick,
    handleDeleteRequest,
    disableDeleteTooltip,
    handleUpdateFromPPRequest,
    setAddMode,
    rows,
    updateUserStatus,
    setSearchText,
  } = props
  const [inputValue, setInputValue] = useState('')
  const {
    orderBy,
    page,
    perPage: rowsPerPage,
  } = useSelector(selectUsersFilters)
  const totalCount = useSelector(selectUsersTotalCount)
  const ssoEnabled = useWWMSSOEnabled()
  const hasSSOErrors = React.useMemo(() => {
    return (
      ssoEnabled &&
      rows.some(
        (user) =>
          user.isCoreProductEnabled === false &&
          user.isActive === true &&
          user.isSSOMappedUser,
      )
    )
  }, [ssoEnabled, rows])

  const dispatch = useAppDispatch()
  const tidalThemeActive = useTidalThemeActive()
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const { isAdmin } = useSelector(selectCurrentUser)

  const {
    loading: colsLoading,
    columns,
    columnVisibility,
    apiRef,
  } = useGetColumnData({
    handleEditClick,
    handleDeleteRequest,
    disableDeleteTooltip,
    handleUpdateFromPPRequest,
    updateUserStatus,
    hasSSOErrors,
  })

  const {
    onColumnWidthChange,
    onSaveColumnState,
    onSortModelChange,
    sortModel,
    handlePaginationModelChange,
  } = useActions({ columns, orderBy })

  const densityModeUserGeneralPreference = userGeneralPreferences?.find(
    (row) => row?.type == IUserPreferenceGeneralListItemType.UsersDensityMode,
  )

  const pageSizeUserGeneralPreference = userGeneralPreferences?.find(
    (row) => row?.type == IUserPreferenceGeneralListItemType.UsersPageSize,
  )

  const perPage = Number(pageSizeUserGeneralPreference?.value || rowsPerPage)
  const stringValue = densityModeUserGeneralPreference?.value ?? 'standard'
  const densityMode = stringValue as GridDensity

  useEffect(() => {
    const savedPageSize = Number(pageSizeUserGeneralPreference?.value)
    if (savedPageSize && savedPageSize !== rowsPerPage) {
      dispatch(setUsersPerPage(savedPageSize))
    }
  }, [pageSizeUserGeneralPreference, rowsPerPage, dispatch])

  const onStateChange = (state: GridState) => {
    if (state.density) {
      onDensityChange(state.density.value)
    }
  }

  const handleAddClick = useCallback(
    (mode: Mode): void => {
      getLicensingInfo(mode)
      setAddMode(true)
    },
    [getLicensingInfo, setAddMode],
  )

  const onDensityChange = (density: string) => {
    if (densityMode != density) {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.UsersDensityMode,
          value: density,
        }),
      )
    }
  }

  const debouncedSetSearchText = useMemo(
    () =>
      debounce((value: string) => {
        setSearchText(value)
      }, 400),
    [setSearchText],
  )

  const handleSearchChange = async (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const value = e.target.value

    setInputValue(value)
    debouncedSetSearchText(value)
    dispatch(setUsersPage(1))
  }

  return (
    <Box minHeight={'calc(100svh - 78px)'} bgcolor={'#F9FAFB'}>
      <Box px={2} pt={2}>
        <Stack
          width="100%"
          direction={{ sm: 'column', md: 'row' }}
          justifyContent={{ sm: 'flex-start', md: 'space-between' }}
          bgcolor={'#E7F5F6'}
          pr={1.7}
          pl={1.2}
          py={1.2}
          borderRadius="8px"
          gap="15px"
          mb={2}
          sx={{
            border: '1px solid #E0E0E0',
          }}
        >
          <Stack display={'flex'} direction="row" alignItems="center">
            <PeopleTalk size={60} />
            <Stack ml={3}>
              <Typography variant="h6">Manage Users & Access List</Typography>
              <Box
                alignItems={'center'}
                display={'flex'}
                flexDirection={'row'}
                gap={0.25}
              >
                <Typography
                  sx={{ color: (theme) => theme.palette.text.primary }}
                  variant="body2"
                >
                  Unmapped and Mapped Users
                </Typography>
                <LightTooltip
                  title={
                    <Typography
                      variant="body2"
                      letterSpacing="0.17px"
                      padding={0.5}
                    >
                      <strong>Mapped employees </strong>
                      are directly linked to PestPac employee accounts. They can
                      use their PestPac credentials to log into the WorkWave
                      Marketing Platform. <strong>
                        Unmapped employees
                      </strong>{' '}
                      are not linked to PestPac employee accounts. They do not
                      have access to PestPac.
                    </Typography>
                  }
                >
                  <InfoOutlinedIcon
                    sx={{
                      color: (theme) => theme.palette.text.primary,
                      fontSize: '18px',
                      mb: 0.3,
                    }}
                    data-test-id="usersUnmappedEmployeesInfoIcon"
                  />
                </LightTooltip>
              </Box>
            </Stack>
          </Stack>

          <Stack
            display={'flex'}
            justifyContent={{ sm: 'flex-start', md: 'flex-end' }}
            direction="row"
            alignItems="center"
            gap={2}
            minWidth={tidalThemeActive ? '407px' : '395px'}
          >
            <ButtonWithTooltip
              sx={{ minWidth: 'fit-content' }}
              color="primary"
              disabled={addDisabled}
              showTooltip={addDisabled}
              tooltipTitle={addDisabledText}
              variant="contained"
              onClick={() => handleAddClick('unmapped')}
              dataTestId="addUnmUsrBtn"
              startIcon={<Add />}
            >
              Add unmapped user
            </ButtonWithTooltip>
            <LoadingButton
              style={{ minWidth: 'fit-content' }}
              loading={loadPending}
              disabled={addDisabled || rolesLoadingStatus === 'fail'}
              showTooltip={addDisabled}
              tooltipTitle={addDisabledText}
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => handleAddClick('mapped')}
            >
              Add mapped user
            </LoadingButton>
          </Stack>
        </Stack>
        {isAdmin ? <LicenseInformation /> : null}
      </Box>

      <TextField
        sx={{ position: 'absolute', right: '20px', zIndex: 1000 }}
        onChange={handleSearchChange}
        value={inputValue}
        placeholder="Search..."
        spellCheck={false}
        variant="standard"
        type="search"
        InputProps={{
          sx: {
            padding: (theme) => `${theme.spacing(0.9)} ${theme.spacing(1.2)}`,
            '&::after': {
              borderBottom: '1px solid #',
            },
            '&::before': {
              borderBottom: '1px solid #ffffff00',
            },
            borderBottom: 'none',
          },
          startAdornment: (
            <InputAdornment position="start">
              <IconButton size="small">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <WWMXGrid
        virtualScrollerHeight={
          densityMode === 'compact'
            ? 369
            : densityMode === 'standard'
              ? 386
              : 402
        }
        applyCustomStyles={true}
        apiRef={apiRef}
        loading={loadPending || usersLoading}
        // columns
        columnVisibilityModel={columnVisibility}
        columns={colsLoading ? [] : columns}
        onColumnWidthChange={(e) =>
          onColumnWidthChange(e?.colDef, columns, columnVisibility)
        }
        columnsSelectorProps={{
          saveCallback: (e) => onSaveColumnState(e, columns),
          defaultColumns: defaultUsersColumns,
          defaultVisibility: defaultUsersColVis,
          lockedColumns: ['actions', 'status', 'errors', 'employee'],
        }}
        pinnedColumns={{ left: ['actions', 'status', 'errors'] }}
        //state
        isDensityEnable
        density={densityMode}
        onStateChange={onStateChange}
        // rows
        rows={colsLoading ? [] : rows}
        rowCount={totalCount}
        // pagination
        paginationModel={{ page: page - 1, pageSize: perPage }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        // sorting
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        width="100%"
        hideDefaultBtn={true}
      />
    </Box>
  )
}
